import {
  AvailableFrequency,
  LinkProps,
  ProductGroupCard,
  ProductGroupCardLayout,
  ProductGroupProps,
  ProductGroupSection,
  ProductGroupSelectorOptions,
  ProductModel,
  ProductType,
  StockActionType,
  useStock,
} from '@mfb/cookbook';
import {ProteinSelectorProps, isNotNullOrUndefined} from '@mfb/lego';
import {graphql, StaticQuery} from 'gatsby';
import {first, isEqual} from 'lodash';
import * as React from 'react';

import {
  discountObjectStorageKey,
  discountStorageKey,
} from '../../templates/Layout';
import {ContentfulBaseProps} from '../ContentfulBaseProps';
import {getProductModel, ProductModelExtended} from '../product/getProductModel';
import {AllProductsQueryModel} from '../productCard/AllProductsQueryModel';
import {
  getProductGroupModel,
  getAddonSelectors,
  getRequiredProteinQuantity,
  shouldHidePrice,
} from './getProductGroupModel';
import {getProductSelectEventPath} from './getProductSelectEventPath';
import {findContentfulProduct} from './findContentfulProduct';
import {findProductForSelection} from './findProductForSelection';
import {ProductGroupContentModel} from './ProductGroupContentModel';
import {ApimClientConfiguration, WebsiteApiClient} from '../../../gatsbyLib/data/WebsiteApiClient';
import {fetchFromWebsiteApi} from '../../../gatsbyLib/data/fetchData';
import { CHRISTMAS_SKUS } from './constants/christmasSkus';

interface ContentfulProductGroupRenderOption {
  renderAs?: 'card' | 'section';
}

type Props = ContentfulBaseProps &
  ContentfulProductGroupRenderOption &
  AllProductsQueryModel;

const PureContentfulProductGroup: React.FC<Props> = ({
  baseModel,
  pageSettings,
  renderAs,
  site,
  allProductDb,
}) => {
  const model = baseModel as ProductGroupContentModel;

  // Added this to retaine the skus after protein selection
  let queryParamSku : string | null = null;
  if(typeof window !== 'undefined'){
    queryParamSku = new URLSearchParams(window.location.search).get('sku');
  }

  const [products] = React.useState<Array<ProductModel>>(() =>
    model.products
      .map(p => getProductModel(p, allProductDb, site.siteMetadata.gatewayUrl))
      .filter(isNotNullOrUndefined)
  );

const [, dispatch] = useStock();


const [productSkusWithStockSoldOut, setproductSkusWithStockSoldOut] = React.useState<Array<string>>([]);

React.useEffect(() => {

  const getSelectedProductStock = async () =>{
    try {
      const config = new ApimClientConfiguration();
    
      config.setApiKey(process.env.GATSBY_WEBSITE_API_KEY as string, '', '')
    
      const client = new WebsiteApiClient(
        config,
        process.env.GATSBY_WEBSITE_API_URL,
        fetchFromWebsiteApi
      );

      const skus = products.map(x => x.sku).join(',');

      return  await client.productsStockByskusAvailability(skus).then(x => x);
    } catch (e) {
    // eslint-disable-next-line no-console
    console.log(`Failed to load primary product addons. ${e.message}`);
    }
  }
  
  getSelectedProductStock().then(x => {
    const skus = x?.filter(y => !y.hasStockAvailable).map(y => y.sku);
    setproductSkusWithStockSoldOut(skus??[]);
  });
},[])

React.useEffect(() => {
  // If the default product is sold out, we need to select a new product and update the dessert and protein accordingly
  if (productSkusWithStockSoldOut.includes(selectedProduct?.sku ?? '')) {
    const newProduct = getSelectedProduct(model, products, queryParamSku);
    setSelectedProduct(newProduct);
    setSelectedDessert(getPreselectedDessert(model, newProduct));
    setSelectedProteins(getPreSelectedProtein(model, newProduct));
  }
}, [productSkusWithStockSoldOut]);

  const getSelectedProduct =  (model:ProductGroupContentModel, products: ProductModelExtended[], selectedSku?: string | null, selectedDessert?: string, selectedProtein?: string[]) => {

  // return first product if there is only one product
  if (products.length === 1){
    return first(products)
  }

  // if protein and dessert is user-selected, return the matching sku
  const selectedProduct = findProductForSelection(products, selectedProtein, selectedDessert);


  if (selectedProduct) {
    return selectedProduct
  }

  // if sku is passed in url (i.e. see menu page), select that sku
  if (selectedSku){
    return first(products.filter(product => product.sku === selectedSku))
  }

  //otherwise select default products configured in contentful if not sold out
  const defaultProteins = model.defaultProductGroupProteins?.map(x => x.proteinName).filter(isNotNullOrUndefined)?.sort() ?? [];
  const defaultDessert = first(model.defaultProductGroupDesserts)?.proteinName ?? '';

  const available = model.isSoldOut
    ? []
    : products.filter(
        product =>
          !productSkusWithStockSoldOut.includes(product.sku) && !product.isSoldOut
      );

  const defaultSku = findProductForSelection(available, defaultProteins, defaultDessert);

  if (defaultSku) {
    return defaultSku;
  }

  //otherwise iterate through the christmas skus and select the first one that is not sold out
  const fallbackProduct = products.find(
    product => (CHRISTMAS_SKUS.includes(product.sku) || CHRISTMAS_SKUS.includes(product.itemNumber)) && !productSkusWithStockSoldOut.includes(product.sku) && !model.isSoldOut && !product.isSoldOut
  ) ?? first(products);

  return fallbackProduct;
   
};

const productSelectEvent: LinkProps = {
  generateLink: (sku: string) => {
    const product = products.find(p => p.sku === sku);
    const extraPath = `${site.siteMetadata.accountUrl}/manage/subscriptions/extras/addbysku/${sku}`;

    return getProductSelectEventPath(extraPath, isPrimary, product);
  },
};

const getPreSelectedProtein = (model:ProductGroupContentModel, selectedProduct?: ProductModelExtended) => {
  return findContentfulProduct(model.products, selectedProduct)?.protein ?? []
}

const getPreselectedDessert = (model: ProductGroupContentModel, selectedProduct?: ProductModelExtended) => {
  return first(findContentfulProduct(model.products, selectedProduct)?.dessert) ?? '';
};

const updateSelectionOrder = (currentSelectionOrder: string[], newSelection: string[]) =>{
  const filteredSelectionArray = currentSelectionOrder.filter(item => newSelection.includes(item));

  // Find the elements in the new array that are not already in the filtered old array
  const newElements = newSelection.filter(item => !filteredSelectionArray.includes(item));

  // Concatenate the new elements to the filtered old array
  const updatedSelectionOrder = filteredSelectionArray.concat(newElements);

  return updatedSelectionOrder;
}

  const [selectedProduct, setSelectedProduct] = React.useState(getSelectedProduct(model, products, queryParamSku));
  const [selectedProteins, setSelectedProteins] = React.useState<Array<string>>(getPreSelectedProtein(model, selectedProduct));
  const [selectedDessert, setSelectedDessert] = React.useState<string>(getPreselectedDessert(model, selectedProduct));
  const [selectionOrder, setSelectionOrder] = React.useState(selectedProteins);
  const productGroupModel = getProductGroupModel(model, products.filter(x => x.sku === selectedProduct?.sku));

  const proteinSelectionChanged = (newSelectedProteins?: string[]) => {
    if (!newSelectedProteins) return;
    if (isEqual(selectedProteins, newSelectedProteins.sort())) return;
    setSelectionOrder(updateSelectionOrder(selectedProteins, newSelectedProteins));
    setSelectedProteins(newSelectedProteins);
  };

  const dessertSelectionChanged = (newSelectedDessert?: string[]) => {
    if(!newSelectedDessert){
      return;
    }

    if (isEqual([selectedDessert], newSelectedDessert)) return;
    if (newSelectedDessert.length > 0) {
      setSelectedDessert(newSelectedDessert[0]);
    }
  };

  // Update product if user changes proteins or dessert
  React.useEffect(() => {
      const matched = findProductForSelection(products, selectedProteins, selectedDessert);

      if (matched) {
        setSelectedProduct(matched);
      }
  }, [selectedProteins, selectedDessert]);

  const proteinSelectorProp = getAddonSelectors(model.productGroupProtein, selectedProteins);
  const dessertSelector = getAddonSelectors(model.productGroupDessert, [selectedDessert]);

  const isBufferStockSoldOut = productSkusWithStockSoldOut.includes(selectedProduct?.sku ?? '');

  const requiredProteinQuantity = getRequiredProteinQuantity(model);

  const proteinSelector: ProteinSelectorProps | undefined = proteinSelectorProp ? {
    maxCheckCount: requiredProteinQuantity,
    proteinSelectors: proteinSelectorProp,
    proteinSelectionOrder: selectionOrder,
    onClick: proteinSelectionChanged,
  } : undefined;

  const props: ProductGroupProps & ProductGroupSelectorOptions = {
    ...productGroupModel,
    description: productGroupModel.description,
    hidePrice: shouldHidePrice(products.length, proteinSelector),
    showSelector: !(CHRISTMAS_SKUS.includes(selectedProduct?.sku ?? '') || CHRISTMAS_SKUS.includes(selectedProduct?.itemNumber ?? '')), //hardcoded for xmas bags
    proteinSelector,
    dessertSelector: dessertSelector ? {
      maxCheckCount: 1,
      proteinSelectors: dessertSelector,
      onClick: dessertSelectionChanged,
    } : undefined,
    preselectedPeople: pageSettings && pageSettings.preselectedPeople,
    preselectedDinners: pageSettings && pageSettings.preselectedDinners,
    productType: selectedProduct?.productType ?? ProductType.Primary,
    productSelectEvent,
    position: model.position,
    isSoldOut: isBufferStockSoldOut || model.isSoldOut || selectedProduct?.isSoldOut, //checks if buffer stock is sold out, if the product group is sold out in Contentful, or if the individual sku is sold out in Contentful
  };

  const renderAsSection = renderAs === 'section' || false;

  const availableFreq = selectedProduct && selectedProduct?.availableFrequency;


  React.useEffect(() => {
    if (availableFreq && availableFreq === AvailableFrequency.OneOff) {
      products.forEach(p =>
        dispatch({type: StockActionType.registerOneOff, sku: p.sku})
      );
    }
  }, [availableFreq, dispatch, products]);

  React.useEffect(() => {
    if (availableFreq && availableFreq === AvailableFrequency.OneOff) {
      // xmas bag
      if (selectedProduct && selectedProduct.productType === ProductType.Primary) {
        // remove any promos
        localStorage.removeItem(discountStorageKey);
        localStorage.removeItem(discountObjectStorageKey);
      }
    }
  }, [availableFreq, selectedProduct]);

  const isPrimary = selectedProduct?.productType === ProductType.Primary;

  // filter out recipies with surcharge
  productGroupModel.products.map(p => {
    p.recipeCollections = p.recipeCollections?.map(rc => {
      rc.collections = rc.collections.map(c => {
        c.recipes = c.recipes.filter(r => r.surcharge === 0);

        return c;
      });

      return rc;
    });

    return p;
  });


  const overrideForExtraCards = {
    // Don't show selector and remove description
    showSelector: false,
    products: props.products.map((p: ProductModel) => ({
      ...p,
      description: '',
    })),
  };

  return renderAsSection ? (
    <>
      <ProductGroupSection {...props} isRecipeModalEnabled={true} />
    </>
  ) : (
    <ProductGroupCard
      {...props}
      {...(isPrimary ? {} : overrideForExtraCards)}
      cardType={
        ProductGroupCardLayout[
          model.cardType as keyof typeof ProductGroupCardLayout
        ]
      }
    />
  );
};

const ContentfulProductGroup: React.FunctionComponent<ContentfulBaseProps &
  ContentfulProductGroupRenderOption> = props => (
  <StaticQuery
    query={graphql`
      query {
        allProductDb {
          edges {
            node {
              ...ProductDbFragment
            }
          }
        }
        site {
          ...SiteMetadataFragment
        }
        features {
          ...FeaturesFragment
        }
      }
    `}
    render={(data: AllProductsQueryModel) => (
      <PureContentfulProductGroup {...props} {...data} />
    )}
  />
);

export default ContentfulProductGroup;
